import FloatingInput from "../fields/FloatingInput";
import CheckboxGroup from "../fields/CheckboxGroup";
import CommuneCombobox from "../fields/CommuneCombobox";
import TurnstileWidget from "@/components/Turnstile/TurnstileWidget";
import {
  CONSENT_TRAITEMENT_TEXT,
  CONSENT_MARKETING_TEXT,
  CONSENT_MARKETING_BENEFIT,
  CONSENT_MARKETING_REASSURANCE,
  LEGAL_MENTION,
  LEGAL_MENTION_URL,
} from "@/lib/consent";

interface StepInfosProps {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  codePostal: string;
  askCodePostal: boolean;
  ville: string;
  onCommuneChange: (codePostal: string, ville: string) => void;
  onCommunesLoaded: (count: number | null) => void;
  contactPreference: string[];
  optin: boolean;
  marketingOptin: boolean;
  onChange: (name: string, value: string) => void;
  onContactPrefChange: (selected: string[]) => void;
  onOptinChange: (checked: boolean) => void;
  onMarketingOptinChange: (checked: boolean) => void;
  errors: Record<string, string>;
  turnstileSiteKey?: string;
  turnstileToken: string;
  onTurnstileToken: (token: string) => void;
  onTurnstileExpired: () => void;
}

export default function StepInfos({
  nom,
  prenom,
  email,
  telephone,
  codePostal,
  askCodePostal,
  ville,
  onCommuneChange,
  onCommunesLoaded,
  contactPreference,
  optin,
  marketingOptin,
  onChange,
  onContactPrefChange,
  onOptinChange,
  onMarketingOptinChange,
  errors,
  turnstileSiteKey,
  turnstileToken: _turnstileToken,
  onTurnstileToken,
  onTurnstileExpired,
}: StepInfosProps) {
  return (
    <fieldset style={{ border: "none" }}>
      <legend
        style={{
          fontSize: 16,
          fontWeight: 500,
          marginBottom: 12,
          display: "block",
          width: "100%",
        }}
      >
        Vos informations
      </legend>

      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <FloatingInput
            name="nom"
            label="Nom"
            value={nom}
            onChange={onChange}
            error={errors.nom}
            required
          />
        </div>
        <div style={{ flex: 1 }}>
          <FloatingInput
            name="prenom"
            label="Prénom"
            value={prenom}
            onChange={onChange}
            error={errors.prenom}
            required
          />
        </div>
      </div>

      <FloatingInput
        name="email"
        label="Email"
        type="email"
        value={email}
        onChange={onChange}
        error={errors.email}
        required
      />

      <FloatingInput
        name="telephone"
        label="Téléphone"
        type="tel"
        value={telephone}
        onChange={onChange}
        error={errors.telephone}
        required
      />

      {askCodePostal && (
        <CommuneCombobox
          codePostal={codePostal}
          ville={ville}
          onChange={onCommuneChange}
          onCommunesLoaded={onCommunesLoaded}
          error={errors.codePostal ?? errors.ville}
          hint="Renseignez le code postal du lieu des travaux, et non celui de votre résidence principale si celui-ci diffère."
        />
      )}

      <CheckboxGroup
        label="À quel moment préférez vous être recontacté ?"
        options={["Matin", "Début d'après-midi (14h00 - 16h00)", "Fin d'après-midi (16h00 - 18h00)"]}
        selected={contactPreference}
        onChange={onContactPrefChange}
      />

      {/* Case 1 — consentement de traitement (obligatoire) */}
      <div style={{ marginTop: 12 }}>
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={optin}
            onChange={(e) => onOptinChange(e.target.checked)}
            style={{
              marginTop: 3,
              width: 18,
              height: 18,
              accentColor: "var(--color-yellow)",
              flexShrink: 0,
            }}
          />
          <span>{CONSENT_TRAITEMENT_TEXT} *</span>
        </label>
        {errors.optin && (
          <span
            style={{
              fontSize: 13,
              padding: "4px 8px",
              marginTop: 4,
              display: "inline-block",
              background: "var(--color-error-bg)",
              color: "var(--color-error-text)",
            }}
          >
            {errors.optin}
          </span>
        )}
      </div>

      {/* Case 2 — consentement marketing (facultatif) */}
      <div style={{ marginTop: 12 }}>
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={marketingOptin}
            onChange={(e) => onMarketingOptinChange(e.target.checked)}
            style={{
              marginTop: 3,
              width: 18,
              height: 18,
              accentColor: "var(--color-yellow)",
              flexShrink: 0,
            }}
          />
          <span>
            {CONSENT_MARKETING_TEXT}
            <br />
            <span style={{ fontSize: 13, opacity: 0.85 }}>
              {CONSENT_MARKETING_BENEFIT} {CONSENT_MARKETING_REASSURANCE}
            </span>
          </span>
        </label>
      </div>

      {/* Mention légale (secondaire) */}
      <p
        style={{
          marginTop: 12,
          fontSize: 11,
          lineHeight: 1.4,
          opacity: 0.7,
        }}
      >
        {LEGAL_MENTION}{" "}
        <a href={LEGAL_MENTION_URL} target="_blank" rel="noopener noreferrer">
          Plus d'informations
        </a>
      </p>

      {turnstileSiteKey ? (
        <div style={{ marginTop: 16 }}>
          <TurnstileWidget
            siteKey={turnstileSiteKey}
            onToken={onTurnstileToken}
            onExpired={onTurnstileExpired}
            onError={onTurnstileExpired}
          />
          {errors.turnstile && (
            <span
              style={{
                fontSize: 13,
                padding: "4px 8px",
                marginTop: 4,
                display: "inline-block",
                background: "var(--color-error-bg)",
                color: "var(--color-error-text)",
              }}
            >
              {errors.turnstile}
            </span>
          )}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 16,
          fontSize: 12,
          lineHeight: "18px",
          color: "#999",
        }}
      >
        <p>* Ces champs sont obligatoires</p>
      </div>
    </fieldset>
  );
}
